import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a New Zealand invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A standard New Zealand invoice must include: your business name and address; the customer name and address; a unique invoice number; the invoice date; a description of goods or services; the amount charged; and your payment details. If you are GST-registered, the invoice must also include the words "Tax Invoice", your GST number, the GST rate and amount, and the total including GST.',
      },
    },
    {
      '@type': 'Question',
      name: 'When do I need to charge GST in New Zealand?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You must register for GST and charge 15% GST once your annual taxable turnover exceeds NZD $60,000. Below this threshold, GST registration is optional. Once registered, you must issue tax invoices showing the GST breakdown on all taxable supplies.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the GST rate in New Zealand?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The GST rate in New Zealand is 15%. It applies to most goods and services. Some supplies are zero-rated (0% GST), such as exports and certain financial services. Invilic automatically calculates 15% GST when you select the GST tax invoice type.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do freelancers in New Zealand invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'When invoicing overseas clients, New Zealand freelancers typically invoice in USD, GBP, EUR, or the agreed currency. Services exported to non-residents are generally zero-rated for GST — you do not charge GST on those invoices. Use the international invoice type in Invilic to bill in any currency with SWIFT or bank transfer details.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a free New Zealand invoice online?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is a free invoice generator for New Zealand businesses and freelancers. Create GST tax invoices with 15% GST auto-calculated, or standard invoices if below the GST threshold. Download a professional PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'GST Tax Invoice',
    subtitle: 'For GST-registered NZ businesses',
    features: ['15% GST auto-calculated', 'GST number field included', 'Net + GST + total breakdown', 'IRD-compliant format', '"Tax Invoice" label included'],
    to: '/create/vat',
    color: 'emerald',
    btnClass: 'bg-emerald-600 hover:bg-emerald-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For businesses below NZD $60,000',
    features: ['No GST required', 'NZD currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, GBP, EUR billing', 'Zero-rated export invoices', 'SWIFT / bank transfer details', 'Multi-currency support'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorNewZealandPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator New Zealand: Free GST Tax Invoice Maker | Invilic"
        description="Free invoice generator for New Zealand businesses and freelancers. Create GST tax invoices with 15% GST auto-calculated or standard invoices. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-new-zealand"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇳🇿 New Zealand
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator New Zealand
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for New Zealand businesses, freelancers, and sole traders. Create GST tax invoices with 15% GST auto-calculated, or standard invoices if you are below the NZD $60,000 threshold. Download professional PDF invoices instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200">
              Create GST Tax Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">GST Tax Invoice vs Standard Invoice in New Zealand</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-emerald-700 dark:text-emerald-400 mb-3">Use a GST Tax Invoice if:</div>
              {[
                'Your annual taxable turnover exceeds NZD $60,000',
                'You are registered for GST with IRD',
                'Your customer needs a tax invoice to claim GST input credits',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your turnover is below the NZD $60,000 GST registration threshold',
                'You are a freelancer or sole trader not yet GST-registered',
                'Your customer does not require a GST tax invoice',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">New Zealand Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your New Zealand Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for NZ businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
